/**
 * Wire-контракт службы дублей (`Duplicates.Contracts`) и его проверка на границе: неожиданная форма — `invalid-response`,
 * а не пустой список.
 */
import { flag, fail, integer, list, oneOf, optionalInteger, optionalText, record, text } from '../../../shared/decode'
import {
  COPY_SIDES,
  MATCH_KINDS,
  PAIR_FILTERS,
  PAIR_STATES,
  SCAN_STATES,
  SCAN_STEPS,
  STEP_PHASES,
  STEP_STATES,
  type DecisionResult,
  type DuplicateItem,
  type DuplicatePair,
  type DuplicateSource,
  type PairCounters,
  type PairsPage,
  type Scan,
  type ScanError,
  type ScanStatus,
  type ScanStepProgress,
  type ScanSummary,
} from '../model/types'

function decodeItem(raw: unknown, name: string): DuplicateItem {
  const item = record(raw, name)
  return {
    key: text(item.key, `${name}.key`),
    name: text(item.name, `${name}.name`),
    code: optionalText(item.code, `${name}.code`),
    sku: optionalText(item.sku, `${name}.sku`),
    barcode: optionalText(item.barcode, `${name}.barcode`),
    moreBarcodes: integer(item.moreBarcodes, `${name}.moreBarcodes`),
    group: optionalText(item.group, `${name}.group`),
    markedCopyInPairId: optionalInteger(item.markedCopyInPairId, `${name}.markedCopyInPairId`),
  }
}

function decodePair(raw: unknown, name: string): DuplicatePair {
  const item = record(raw, name)
  const copySide = optionalText(item.copySide, `${name}.copySide`)
  return {
    id: integer(item.id, `${name}.id`),
    first: decodeItem(item.first, `${name}.first`),
    second: decodeItem(item.second, `${name}.second`),
    percent: integer(item.percent, `${name}.percent`),
    matchedBy: oneOf(item.matchedBy, MATCH_KINDS, `${name}.matchedBy`),
    sharedWords: list(item.sharedWords, `${name}.sharedWords`, (entry, field) => (typeof entry === 'string' ? entry : fail(`${field} — не строка`))),
    state: oneOf(item.state, PAIR_STATES, `${name}.state`),
    copySide: copySide === null ? null : oneOf(copySide, COPY_SIDES, `${name}.copySide`),
    decidedBy: optionalText(item.decidedBy, `${name}.decidedBy`),
    decidedAt: optionalText(item.decidedAt, `${name}.decidedAt`),
    firstGone: flag(item.firstGone, `${name}.firstGone`),
    secondGone: flag(item.secondGone, `${name}.secondGone`),
  }
}

function decodeCounters(raw: unknown, name: string): PairCounters {
  const item = record(raw, name)
  return {
    all: integer(item.all, `${name}.all`),
    undecided: integer(item.undecided, `${name}.undecided`),
    marked: integer(item.marked, `${name}.marked`),
    notDuplicate: integer(item.notDuplicate, `${name}.notDuplicate`),
  }
}

export function decodePairsPage(raw: unknown): PairsPage {
  const page = record(raw, 'pairs')
  return {
    resourceId: text(page.resourceId, 'pairs.resourceId'),
    scanId: optionalText(page.scanId, 'pairs.scanId'),
    filter: oneOf(page.filter, PAIR_FILTERS, 'pairs.filter'),
    page: integer(page.page, 'pairs.page'),
    pageSize: integer(page.pageSize, 'pairs.pageSize'),
    total: integer(page.total, 'pairs.total'),
    counters: decodeCounters(page.counters, 'pairs.counters'),
    items: list(page.items, 'pairs.items', decodePair),
  }
}

export function decodeDecisionResult(raw: unknown): DecisionResult {
  const result = record(raw, 'decision')
  return { pair: decodePair(result.pair, 'decision.pair'), counters: decodeCounters(result.counters, 'decision.counters') }
}

function decodeStep(raw: unknown, name: string): ScanStepProgress {
  const item = record(raw, name)
  const phase = optionalText(item.phase, `${name}.phase`)
  return {
    step: oneOf(item.step, SCAN_STEPS, `${name}.step`),
    state: oneOf(item.state, STEP_STATES, `${name}.state`),
    phase: phase === null ? null : oneOf(phase, STEP_PHASES, `${name}.phase`),
    done: integer(item.done, `${name}.done`),
    total: optionalInteger(item.total, `${name}.total`),
    sent: optionalText(item.sent, `${name}.sent`),
    awaiting: optionalText(item.awaiting, `${name}.awaiting`),
    received: optionalText(item.received, `${name}.received`),
    startedAt: optionalText(item.startedAt, `${name}.startedAt`),
    finishedAt: optionalText(item.finishedAt, `${name}.finishedAt`),
    retryAt: optionalText(item.retryAt, `${name}.retryAt`),
  }
}

function decodeScanError(raw: unknown, name: string): ScanError | null {
  if (raw === null || raw === undefined) return null
  const item = record(raw, name)
  return {
    step: oneOf(item.step, SCAN_STEPS, `${name}.step`),
    code: text(item.code, `${name}.code`),
    // Текст ошибки — дословный ответ источника или службы (ADR-8): пустым он быть не может.
    message: text(item.message, `${name}.message`),
    requestId: optionalText(item.requestId, `${name}.requestId`),
    at: text(item.at, `${name}.at`),
  }
}

function decodeScan(raw: unknown, name: string): Scan | null {
  if (raw === null || raw === undefined) return null
  const item = record(raw, name)
  const currentStep = optionalText(item.currentStep, `${name}.currentStep`)
  return {
    id: text(item.id, `${name}.id`),
    resourceId: text(item.resourceId, `${name}.resourceId`),
    state: oneOf(item.state, SCAN_STATES, `${name}.state`),
    currentStep: currentStep === null ? null : oneOf(currentStep, SCAN_STEPS, `${name}.currentStep`),
    threshold: integer(item.threshold, `${name}.threshold`),
    steps: list(item.steps, `${name}.steps`, decodeStep),
    itemCount: integer(item.itemCount, `${name}.itemCount`),
    pairCount: integer(item.pairCount, `${name}.pairCount`),
    markedCount: integer(item.markedCount, `${name}.markedCount`),
    error: decodeScanError(item.error, `${name}.error`),
    startedAt: text(item.startedAt, `${name}.startedAt`),
    finishedAt: optionalText(item.finishedAt, `${name}.finishedAt`),
    canStop: flag(item.canStop, `${name}.canStop`),
    canResume: flag(item.canResume, `${name}.canResume`),
    canRestart: flag(item.canRestart, `${name}.canRestart`),
  }
}

export function decodeScanStatus(raw: unknown): ScanStatus {
  const status = record(raw, 'scan')
  return { resourceId: text(status.resourceId, 'scan.resourceId'), scan: decodeScan(status.scan, 'scan.scan') }
}

function decodeSummary(raw: unknown, name: string): ScanSummary | null {
  if (raw === null || raw === undefined) return null
  const item = record(raw, name)
  return {
    scanId: text(item.scanId, `${name}.scanId`),
    state: oneOf(item.state, SCAN_STATES, `${name}.state`),
    threshold: integer(item.threshold, `${name}.threshold`),
    itemCount: integer(item.itemCount, `${name}.itemCount`),
    pairCount: integer(item.pairCount, `${name}.pairCount`),
    markedCount: integer(item.markedCount, `${name}.markedCount`),
    startedAt: text(item.startedAt, `${name}.startedAt`),
    finishedAt: optionalText(item.finishedAt, `${name}.finishedAt`),
  }
}

export function decodeSources(raw: unknown): DuplicateSource[] {
  const body = record(raw, 'sources')
  return list(body.sources, 'sources.sources', (entry, name) => {
    const item = record(entry, name)
    return {
      id: text(item.id, `${name}.id`),
      kind: text(item.kind, `${name}.kind`),
      displayName: text(item.displayName, `${name}.displayName`),
      lastScan: decodeSummary(item.lastScan, `${name}.lastScan`),
    }
  })
}
